import { CLIENTS } from "./data";
import { currentIsoDate, exportFilename, invoiceNumber, parseCurrency, paymentTermsLabel } from "./invoiceUtils";
import { calculateInvoiceTotals, deriveLineTotalFromInvoiceTotal } from "../utils/invoiceTotals.mjs";
import { documentLabels, formatLocalizedDocumentCurrency, formatLocalizedDocumentDate, normalizeDocumentPreferences } from "./documentPreferences";
import { currencyFractionDigits, roundMoney } from "../utils/money.mjs";
import { resolveInvoiceParties } from './invoiceDocumentIdentity';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

export const getExportData = (invoice, issuer, draft, clients = CLIENTS, documentPreferences = {}) => {
  const parties = resolveInvoiceParties(invoice, issuer ? [issuer] : [], clients, draft);
  // Older explicit export callers supply party objects, before stable issuer IDs existed.
  if (!invoice.issuerId && !draft?.issuerId && !invoice.documentIdentity) {
    const clientId = draft?.clientId || invoice.clientId;
    const matches = clients.filter(item => clientId ? item.id === clientId : item.name === (draft?.client || invoice.client));
    parties.issuer = issuer || null;
    parties.client = matches.length === 1 ? matches[0] : null;
  }
  const client = parties.client || {};
  const taxConfig = {
    taxLines: draft?.taxLines ?? invoice.taxLines,
    adjustments: draft?.adjustments ?? invoice.adjustments,
    currency: draft?.currency || invoice.currency || "EUR",
    taxEnabled: invoice.taxEnabled,
    taxRate: invoice.taxRate,
    taxMode: invoice.taxMode,
    taxLabel: invoice.taxLabel,
  };
  const lineTotalFromInvoice = deriveLineTotalFromInvoiceTotal(parseCurrency(invoice.amount), taxConfig);
  const items = draft?.items?.length ? draft.items.map((item) => ({ description: item.description || "Professional services", quantity: Number(item.quantity || 0), rate: Number(item.rate || 0) })) : invoice.items?.length ? invoice.items.map((item) => ({ description: item.description || "Professional services", quantity: Number(item.quantity || 0), rate: Number(item.rate || 0) })) : [{ description: draft?.description || invoice.work || "Professional services", quantity: Number(draft?.quantity || 1), rate: Number(draft?.rate || lineTotalFromInvoice || 0) }];
  const totals = calculateInvoiceTotals(items, taxConfig);

  return {
    number: invoiceNumber(invoice),
    status: invoice.status || "Draft",
    issuedDate: invoice.issuedDate || currentIsoDate(),
    dueDate: invoice.dueDate || currentIsoDate(),
    terms: paymentTermsLabel(invoice.paymentTerms || "Net 14"),
    issuer: parties.issuer || {},
    client,
    items,
    currency: draft?.currency || invoice.currency || "EUR",
    taxNote: draft?.taxNote ?? invoice.taxNote ?? "",
    reference: draft?.reference ?? invoice.reference ?? "",
    notes: draft?.notes ?? invoice.notes ?? "",
    footerNotes: draft?.footerNotes ?? invoice.footerNotes ?? "",
    reverseChargeEnabled: Boolean(draft?.reverseChargeEnabled ?? invoice.reverseChargeEnabled),
    reverseChargeNote: draft?.reverseChargeNote ?? invoice.reverseChargeNote ?? "",
    documentType: draft?.documentType || invoice.documentType || "invoice",
    documentPreferences: normalizeDocumentPreferences(documentPreferences),
    ...totals,
  };
};

export const buildInvoicePdfBlob = async (invoice, issuer, draft, clients, documentPreferences) => {
  const module = await import(/* webpackChunkName: "prototype-pdf" */ "jspdf");
  const JsPDF = module.jsPDF || module.default?.jsPDF || module.default;
  const pdf = new JsPDF({ unit: "mm", format: "a4" });
  const data = getExportData(invoice, issuer, draft, clients, documentPreferences);
  const labels = documentLabels(data.documentPreferences);
  const money = (value) => formatLocalizedDocumentCurrency(value, data.currency, data.documentPreferences);

  pdf.setFillColor(31, 31, 30);
  pdf.rect(0, 0, 210, 31, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.text(data.documentType === "credit-note" ? labels.creditNote : labels.invoice, 16, 19);
  pdf.setFontSize(11);
  pdf.text(data.number, 194, 19, { align: "right" });

  pdf.setTextColor(36, 34, 31);
  pdf.setFontSize(8);
  pdf.setFont("helvetica", "bold");
  pdf.text(labels.from.toUpperCase(), 16, 46);
  pdf.text(labels.billTo.toUpperCase(), 111, 46);
  pdf.setFontSize(11);
  pdf.text(data.issuer.legalName || data.issuer.name || "Issuing business", 16, 53);
  pdf.text(data.client.name || invoice.client, 111, 53);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(100, 94, 87);
  pdf.text([data.issuer.location || "", data.issuer.email || ""].filter(Boolean), 16, 59);
  pdf.text([data.client.contact || "", data.client.email || ""].filter(Boolean), 111, 59);

  pdf.setDrawColor(225, 217, 208);
  pdf.line(16, 77, 194, 77);
  pdf.setTextColor(100, 94, 87);
  pdf.setFont("helvetica", "bold");
  pdf.text(labels.issued.toUpperCase(), 16, 86);
  pdf.text(labels.due.toUpperCase(), 75, 86);
  if (data.reference) pdf.text(labels.reference.toUpperCase(), 134, 86);
  pdf.setTextColor(36, 34, 31);
  pdf.setFont("helvetica", "normal");
  pdf.text(formatLocalizedDocumentDate(data.issuedDate, data.documentPreferences), 16, 92);
  pdf.text(formatLocalizedDocumentDate(data.dueDate, data.documentPreferences), 75, 92);
  if (data.reference) pdf.text(data.reference, 134, 92);

  pdf.setFillColor(246, 240, 234);
  pdf.rect(16, 107, 178, 10, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(85, 77, 70);
  pdf.text(labels.description.toUpperCase(), 20, 113.5);
  pdf.text(labels.quantity.toUpperCase(), 129, 113.5, { align: "right" });
  pdf.text(labels.rate.toUpperCase(), 160, 113.5, { align: "right" });
  pdf.text(labels.amount.toUpperCase(), 190, 113.5, { align: "right" });
  pdf.setTextColor(36, 34, 31);
  pdf.setFont("helvetica", "normal");
  let itemY = 126;
  data.items.forEach((item, index) => {
    const description = pdf.splitTextToSize(item.description, 90);
    pdf.text(description, 20, itemY);
    pdf.text(String(item.quantity), 129, itemY, { align: "right" });
    pdf.text(money(item.rate), 160, itemY, { align: "right" });
    pdf.text(money(data.lineItems[index]?.amount ?? item.quantity * item.rate), 190, itemY, { align: "right" });
    itemY += Math.max(12, description.length * 5);
  });
  pdf.line(16, itemY + 2, 194, itemY + 2);

  const totals = [
    [data.includedTaxTotal ? labels.taxableSubtotal : labels.subtotal, data.subtotal],
    ...data.taxItems.map((tax) => [`${tax.label} ${tax.rate}%${tax.treatment === "included" ? ` (${labels.included})` : tax.treatment === "withheld" ? ` (${labels.withheld})` : ""}`, tax.treatment === "withheld" ? -tax.amount : tax.amount]),
    ...data.adjustmentItems.map((adjustment) => [adjustment.label || labels.adjustment, adjustment.direction === "add" ? adjustment.amountValue : -adjustment.amountValue]),
    [data.documentType === "credit-note" ? labels.creditTotal : labels.total, data.total],
  ];
  totals.forEach(([label, value], index) => {
    const y = itemY + 13 + index * 9;
    pdf.setFont("helvetica", index === totals.length - 1 ? "bold" : "normal");
    pdf.text(label, 145, y);
    pdf.text(money(value), 190, y, { align: "right" });
  });

  const documentNotes = [data.notes, data.taxNote, data.reverseChargeEnabled ? data.reverseChargeNote : ""].filter(Boolean);
  if (documentNotes.length) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(100, 94, 87);
    pdf.text(pdf.splitTextToSize(documentNotes.join("\n\n"), 118), 16, Math.min(232, itemY + 20 + totals.length * 9));
  }

  pdf.setFillColor(232, 239, 235);
  pdf.rect(16, 246, 178, 25, "F");
  pdf.setTextColor(37, 82, 73);
  pdf.setFont("helvetica", "bold");
  pdf.text(data.footerNotes || labels.thankYou, 21, 257);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.text("Invoice Studio by Coetic X", 21, 264);
  return pdf.output("blob");
};

const downloadPdf = async (invoice, issuer, draft, clients, documentPreferences) => {
  const blob = await buildInvoicePdfBlob(invoice, issuer, draft, clients, documentPreferences);
  downloadBlob(blob, exportFilename(invoice, "pdf"));
};

const downloadExcel = async (invoice, issuer, draft, clients, documentPreferences) => {
  const entry = getExcelExportEntry(invoice, issuer, draft, clients, documentPreferences);
  const { downloadInvoiceWorkbook } = await import(/* webpackChunkName: "invoice-excel" */ "../utils/invoiceWorkbook.mjs");
  await downloadInvoiceWorkbook(entry, exportFilename(invoice, "xlsx"));
};

const csvCell = (value) => {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildInvoiceCsv = (invoice, issuer, draft, clients = CLIENTS, documentPreferences = {}) => {
  const data = getExportData(invoice, issuer, draft, clients, documentPreferences);
  const headers = [
    "Invoice number",
    "Status",
    "Issue date",
    "Due date",
    "Issuer",
    "Client",
    "Description",
    "Quantity",
    "Rate",
    "Line amount",
    "Currency",
    "Subtotal",
    "Tax total",
    "Adjustment total",
    "Invoice total",
  ];
  const rows = data.items.map((item, index) => [
    data.number,
    data.status,
    data.issuedDate,
    data.dueDate,
    data.issuer.legalName || data.issuer.name || "",
    data.client.name || invoice.client || "",
    item.description,
    item.quantity,
    item.rate,
    data.lineItems[index]?.amount ?? Number(item.quantity || 0) * Number(item.rate || 0),
    data.currency,
    data.subtotal,
    data.taxTotal,
    data.adjustmentsTotal,
    data.total,
  ]);
  return [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
};

const downloadCsv = (invoice, issuer, draft, clients, documentPreferences) => {
  const content = `\uFEFF${buildInvoiceCsv(invoice, issuer, draft, clients, documentPreferences)}`;
  downloadBlob(new Blob([content], { type: "text/csv;charset=utf-8" }), exportFilename(invoice, "csv"));
};

const settlementMinor = (value, currency) => {
  const amount = typeof value === "number" ? value
    : typeof value === "string" && /^-?\d+(?:\.\d+)?$/.test(value.trim()) ? Number(value) : NaN;
  const minor = Math.round(roundMoney(amount, currency) * 10 ** currencyFractionDigits(currency));
  if (!Number.isFinite(amount) || !Number.isSafeInteger(minor)) throw new Error("The local settlement amount is invalid. Review the invoice before exporting.");
  return minor;
};

export const getExcelExportEntry = (invoice, issuer, draft, clients = CLIENTS, documentPreferences = {}) => {
  if ([invoice, draft].some(record => record && (Object.hasOwn(record, "accountStatus") || Object.hasOwn(record, "organizationId") || Object.hasOwn(record, "payments")))) {
    throw new Error("Download this invoice through its account or payment-ledger document export, not the local settlement export.");
  }
  const data = getExportData(invoice, issuer, draft, clients, documentPreferences);
  const currency = String(data.currency).trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) throw new Error("Review the invoice currency before exporting.");
  const invoiceTotalMinor = settlementMinor(data.total, currency);
  const paidAmountMinor = settlementMinor(invoice.paidAmount ?? 0, currency);
  const status = String(data.status).trim().toLowerCase();
  const credit = data.documentType === "credit-note" || invoiceTotalMinor < 0;
  const receivableMinor = credit ? 0 : invoiceTotalMinor;
  const receivedMinor = credit ? 0 : status === "paid" ? receivableMinor : Math.min(receivableMinor, Math.max(0, paidAmountMinor));
  const basis = credit ? "credit"
    : invoice.importedPaymentStatus === "Needs review" ? "imported-assumption"
    : status === "paid" ? invoice.importedPaymentStatus === "Confirmed paid" ? "imported-confirmed-paid" : "paid-status"
    : invoice.importedPaymentStatus === "Confirmed not paid" ? "imported-confirmed-unpaid"
    : receivedMinor > 0 ? "paid-amount" : "unpaid";
  return {
    owner: {
      company: data.issuer.legalName || data.issuer.name,
      name: data.issuer.name,
      address: data.issuer.location,
      email: data.issuer.email,
      vatNumber: data.issuer.vatNumber,
    },
    client: {
      company: data.client.name || invoice.client,
      name: data.client.contact,
      email: data.client.email,
      vatNumber: data.client.taxNumber,
    },
    invoice: {
      invoiceNumber: data.number,
      invoiceDate: data.issuedDate,
      dueDate: data.dueDate,
      currency,
      status: status === "partially paid" ? "partial" : status,
      taxLines: data.taxLines,
      taxEnabled: data.taxLines.length === 1,
      taxRate: data.taxLines[0]?.rate || 0,
      taxMode: data.taxLines[0]?.treatment === "included" ? "inclusive" : "exclusive",
      taxLabel: data.taxLines[0]?.label || "Tax",
      adjustments: data.adjustmentItems,
      documentType: data.documentType,
      referenceInvoiceNumber: data.reference,
      notes: [data.notes, data.taxNote, data.reverseChargeEnabled ? data.reverseChargeNote : ""].filter(Boolean).join("\n\n"),
      footerNotes: data.footerNotes,
      documentLanguage: data.documentPreferences.language,
    },
    items: data.items.map((item) => ({ title: item.description, description: "Approved billable work", quantity: item.quantity, rate: item.rate })),
    payments: [],
    localSettlementSummary: {
      kind: "local-invoice-settlement-v1", currency, invoiceTotalMinor, receivedMinor,
      remainingMinor: receivableMinor - receivedMinor, basis,
    },
  };
};

const downloadJson = (invoice, issuer, draft, clients, documentPreferences) => {
  const blob = new Blob([JSON.stringify(getExportData(invoice, issuer, draft, clients, documentPreferences), null, 2)], { type: "application/json" });
  downloadBlob(blob, exportFilename(invoice, "json"));
};

export const downloadInvoice = async ({ invoice, issuer, draft, clients, format, documentPreferences }) => {
  if (format === "pdf") await downloadPdf(invoice, issuer, draft, clients, documentPreferences);
  else if (format === "xlsx") await downloadExcel(invoice, issuer, draft, clients, documentPreferences);
  else if (format === "csv") downloadCsv(invoice, issuer, draft, clients, documentPreferences);
  else if (format === "json") downloadJson(invoice, issuer, draft, clients, documentPreferences);
  else throw new Error(`Unsupported invoice format: ${format}`);
  return exportFilename(invoice, format);
};
